import type { FilterDto } from '@act/shared/data-transfer-objects';
import type { MemberFiltersCacheEntry } from 'redux/types/membersTypes';
import { LoadingStatus } from 'redux/types/sharedTypes';

/** Default session stale window for static catalogs (30 minutes). */
export const REFERENCE_DATA_DEFAULT_STALE_MS = 30 * 60 * 1000;

/**
 * Returns true when the slice already holds successful data within the stale window.
 * @param status - Redux loading status for the resource.
 * @param fetchedAtMs - Epoch ms when data last succeeded; undefined if never.
 * @param staleMs - Max age before refetch; defaults to REFERENCE_DATA_DEFAULT_STALE_MS.
 * @returns Whether callers should skip a new network fetch.
 */
export function isReferenceDataFresh(
  status: LoadingStatus,
  fetchedAtMs: number | undefined,
  staleMs: number = REFERENCE_DATA_DEFAULT_STALE_MS
): boolean {
  if (status !== 'success' || fetchedAtMs == null) {
    return false;
  }
  return Date.now() - fetchedAtMs < staleMs;
}

/**
 * Whether an async-thunk settlement still matches the latest request we tracked.
 * After `resetRedux` / impersonation clears `latestRequestId`, prior in-flight
 * fulfills must not rewrite session catalogs or stamp them fresh.
 * @param requestId - `action.meta.requestId` from the settlement.
 * @param latestRequestId - Id stored on pending for this resource (or key).
 * @returns True when the settlement should update Redux.
 */
export function isLatestTrackedRequest(
  requestId: string | undefined,
  latestRequestId: string | undefined
): boolean {
  return (
    requestId !== undefined &&
    latestRequestId !== undefined &&
    requestId === latestRequestId
  );
}

/** Classification of `getTeam` param variants that must not share in-flight state. */
export type GetTeamFetchKind = 'allViewable' | 'callInvite' | 'bare';

/**
 * Classifies a `getTeam` request so variants do not share in-flight / requestId state.
 * @param params - Optional getTeam query params.
 * @returns Fetch kind for reducer bookkeeping.
 */
export function getTeamFetchKind(params?: {
  allViewable?: boolean;
  allCareStaff?: boolean;
}): GetTeamFetchKind {
  if (params?.allViewable === true && params?.allCareStaff === true) {
    return 'callInvite';
  }
  if (params?.allViewable === true) {
    return 'allViewable';
  }
  return 'bare';
}

/**
 * Normalizes the `named` query param used as a member-filters cache key.
 * @param named - Comma-separated filter field list from the request.
 * @returns Trimmed cache key.
 */
export function memberFiltersCacheKey(named: string): string {
  return named.trim();
}

const EMPTY_MEMBER_FILTERS: FilterDto[] = [];
Object.freeze(EMPTY_MEMBER_FILTERS);

/**
 * Reads filters out of a member-filters cache entry.
 * Returns one shared empty array when the entry is missing so `useSelector`
 * keeps reference equality and does not re-render on every unrelated action.
 * @param entry - Cache entry for the active `named` key, if present.
 * @returns Cached filters, or a stable empty array.
 */
export function memberFiltersFromCacheEntry(
  entry: MemberFiltersCacheEntry | undefined
): FilterDto[] {
  return entry?.filters ?? EMPTY_MEMBER_FILTERS;
}

/**
 * Whether `ensureVerifyUser` should dispatch `verifyUser`.
 * Skips while a verify is already in flight (including `{ force: true }`) so
 * concurrent callers do not each emit `verifyUser.fulfilled` and re-run side effects.
 * `verifyInFlight` is authoritative for in-flight detection: a background re-verify
 * leaves `status` at `success` instead of moving it to `loading`.
 * @param params.status - Acting-user slice loading status
 * @param params.userId - Current acting user id when known
 * @param params.force - When true, refetch even if status is already success
 * @param params.verifyInFlight - Whether a verifyUser request is currently pending
 * @returns True when verifyUser should be dispatched
 */
export function shouldDispatchVerifyUser(params: {
  status: LoadingStatus;
  userId: string | undefined;
  force: boolean;
  verifyInFlight: boolean;
}): boolean {
  const { status, userId, force, verifyInFlight } = params;
  if (verifyInFlight || status === 'loading') {
    return false;
  }
  if (!force && status === 'success' && Boolean(userId)) {
    return false;
  }
  return true;
}
